import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ListChecks, Sparkles } from 'lucide-react';
import { DOCUMENT_CONTENT_QUERY, DOCUMENTS_QUERY } from '../graphql/queries';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { displayTitle, parseListView, typeFromPath } from '../lib/docView';

interface DocumentSummary {
  path: string;
  title: string;
  type: string | null;
  updatedAt: string;
  treeLabel: string;
  spaceId: string | null;
}

interface DocumentsData {
  documents: DocumentSummary[];
}

/** "today", "yesterday" or "Jul 7, 2026" in the local tz. */
function formatUpdated(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'today';
  if (d.toDateString() === y.toDateString()) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Lists page: every list document across the wiki (personal and shared trees)
 * as a read-only card grid, most recently touched first. Every card says
 * which tree it lives in, previews its next open items, and shows when it was
 * last touched (design review C1) — a card earns its tap before you take it.
 * Tapping a card opens the document page, where the interactive checklist
 * (tap-to-toggle, add item) lives.
 */
export function ListsPage() {
  const { data, loading, error } = useQuery<DocumentsData>(DOCUMENTS_QUERY);

  const lists = useMemo(() => {
    const docs = (data?.documents ?? []).filter(
      (doc) => (doc.type ?? typeFromPath(doc.path)) === 'list'
    );
    // Most recently touched first — like a chat list, the active things lead.
    return [...docs].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [data?.documents]);

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <div className="px-1 pt-4 sm:pt-6 pb-2">
        <h1 className="text-2xl font-display font-semibold text-[var(--text-primary)]">Lists</h1>
        <p className="mt-1 text-[var(--text-secondary)]">
          Places to hold things that need tending. Tap a list to open it.
        </p>
      </div>

      {error && !data ? (
        <FetchErrorCard>
          Something went wrong loading your lists. Try again in a moment.
        </FetchErrorCard>
      ) : loading && !data ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-hidden="true">
          <span className="skeleton block h-28 w-full rounded-2xl" />
          <span className="skeleton block h-28 w-full rounded-2xl" />
        </div>
      ) : lists.length === 0 ? (
        <section className="glass-panel rounded-2xl p-8 border border-[var(--card-border)] shadow-soft text-center">
          <ListChecks
            className="w-8 h-8 mx-auto text-[var(--text-tertiary)]"
            aria-hidden
            strokeWidth={1.5}
          />
          <p className="mt-3 text-[var(--text-secondary)]">
            No lists yet. Tell me something like &ldquo;start a grocery list with milk and
            eggs&rdquo; and it will appear here.
          </p>
          <Link to="/alma" className="btn btn-primary mt-4 gap-2">
            <Sparkles className="w-4 h-4" aria-hidden /> Tell me
          </Link>
        </section>
      ) : (
        // grid-cols-1 (i.e. minmax(0,1fr)) matters: a bare auto column lets a
        // card's unbreakable content (a long URL in an item preview) size the
        // track wider than the phone viewport.
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {lists.map((doc) => (
            <ListDocCard key={doc.path} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ListDocCardData {
  documentContent: { path: string; content: string } | null;
}

/** How many open items preview on a card before "…and N more". */
const PREVIEW_ITEMS = 2;

/**
 * Glass card for one list document: icon tile, name, tree chip, a preview of
 * the next open items, and a quiet "N left · updated …" line parsed from the
 * document's checklist. Fail-quiet like the index rows — while the content
 * loads (or on error) the card simply shows no preview.
 */
function ListDocCard({ doc }: { doc: DocumentSummary }) {
  const { data } = useQuery<ListDocCardData>(DOCUMENT_CONTENT_QUERY, {
    variables: { path: doc.path },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const content = data?.documentContent?.content ?? null;
  const view = content === null ? null : parseListView(content);
  const open = view?.items.filter((item) => !item.checked) ?? [];
  const total = view?.items.length ?? 0;
  const updated = formatUpdated(doc.updatedAt);

  const statusLine =
    view === null
      ? null
      : total === 0
        ? 'Nothing here yet'
        : open.length === 0
          ? 'All done'
          : `${open.length} left`;

  return (
    <Link
      to={`/docs/${doc.path}`}
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft hover:shadow-warm transition-shadow block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      data-testid="list-doc-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl bg-[color-mix(in_oklab,var(--marine-400)_15%,transparent)] text-[var(--accent-ink)] flex items-center justify-center shrink-0"
          aria-hidden
        >
          <ListChecks className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-[var(--text-primary)] truncate">
            {displayTitle(doc.path, doc.title)}
          </h2>
          {statusLine && (
            <p className="text-sm text-[var(--text-tertiary)]">
              {statusLine}
              {updated ? ` · ${updated}` : ''}
            </p>
          )}
        </div>
        <span className="text-xs font-medium text-[var(--accent-ink)] bg-[color-mix(in_oklab,var(--marine-400)_12%,transparent)] px-2 py-1 rounded-full shrink-0">
          {doc.treeLabel}
        </span>
      </div>

      {open.length > 0 && (
        <ul className="mt-3 space-y-1" aria-label="Next open items">
          {open.slice(0, PREVIEW_ITEMS).map((item) => (
            <li
              key={item.lineIndex}
              className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)]"
            >
              <span
                className="w-4 h-4 rounded-full border-2 border-[var(--border)] shrink-0"
                aria-hidden
              />
              <span className="truncate">{item.text}</span>
            </li>
          ))}
          {open.length > PREVIEW_ITEMS && (
            <li className="pl-[1.65rem] text-xs text-[var(--text-tertiary)]">
              …and {open.length - PREVIEW_ITEMS} more
            </li>
          )}
        </ul>
      )}
    </Link>
  );
}
